import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, AlertCircle, Calendar, Clock } from 'lucide-react';
import {
  useCreatePaymentSchedule,
  useUpdatePaymentSchedule,
} from '../../hooks/usePaymentSchedules';
import type {
  PaymentScheduleTemplate,
  PaymentScheduleType,
  CreatePaymentScheduleData,
  CreateInstallmentData,
} from '@/types/tuition.types';

interface Props {
  schedule?: PaymentScheduleTemplate | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface InstallmentForm {
  id?: string;
  label: string;
  percentageOfTotal: number;
  dateType: 'fixed' | 'relative';
  dueDayOfMonth?: number;
  dueMonth?: number;
  relativeDays?: number;
}

const SCHEDULE_TYPES: { value: PaymentScheduleType; label: string; defaultInstallments: number }[] = [
  { value: 'ANNUAL', label: 'Annuel', defaultInstallments: 1 },
  { value: 'SEMESTRIAL', label: 'Semestriel', defaultInstallments: 2 },
  { value: 'TRIMESTRIAL', label: 'Trimestriel', defaultInstallments: 4 },
  { value: 'MONTHLY', label: 'Mensuel', defaultInstallments: 10 },
  { value: 'CUSTOM', label: 'Personnalisé', defaultInstallments: 1 },
];

const MONTHS = [
  { value: 1, label: 'Janvier' },
  { value: 2, label: 'Février' },
  { value: 3, label: 'Mars' },
  { value: 4, label: 'Avril' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Juin' },
  { value: 7, label: 'Juillet' },
  { value: 8, label: 'Août' },
  { value: 9, label: 'Septembre' },
  { value: 10, label: 'Octobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Décembre' },
];

// Preset templates for quick selection
const PRESET_TEMPLATES: Record<PaymentScheduleType, InstallmentForm[]> = {
  ANNUAL: [
    { label: 'Paiement unique', percentageOfTotal: 100, dateType: 'fixed', dueDayOfMonth: 15, dueMonth: 9 },
  ],
  SEMESTRIAL: [
    { label: '1er Semestre', percentageOfTotal: 50, dateType: 'fixed', dueDayOfMonth: 15, dueMonth: 9 },
    { label: '2ème Semestre', percentageOfTotal: 50, dateType: 'fixed', dueDayOfMonth: 15, dueMonth: 2 },
  ],
  TRIMESTRIAL: [
    { label: '1er Trimestre', percentageOfTotal: 25, dateType: 'fixed', dueDayOfMonth: 15, dueMonth: 9 },
    { label: '2ème Trimestre', percentageOfTotal: 25, dateType: 'fixed', dueDayOfMonth: 15, dueMonth: 12 },
    { label: '3ème Trimestre', percentageOfTotal: 25, dateType: 'fixed', dueDayOfMonth: 15, dueMonth: 3 },
    { label: '4ème Trimestre', percentageOfTotal: 25, dateType: 'fixed', dueDayOfMonth: 15, dueMonth: 6 },
  ],
  MONTHLY: Array.from({ length: 10 }, (_, i) => ({
    label: `Mois ${i + 1}`,
    percentageOfTotal: 10,
    dateType: 'fixed' as const,
    dueDayOfMonth: 1,
    dueMonth: ((9 + i - 1) % 12) + 1, // Sept to June
  })),
  CUSTOM: [
    { label: 'Échéance 1', percentageOfTotal: 100, dateType: 'fixed', dueDayOfMonth: 1, dueMonth: 9 },
  ],
};

const PaymentScheduleFormModal: React.FC<Props> = ({ schedule, onClose, onSuccess }) => {
  const isEditing = !!schedule;
  const createMutation = useCreatePaymentSchedule();
  const updateMutation = useUpdatePaymentSchedule();

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    type: 'MONTHLY' as PaymentScheduleType,
    description: '',
    isDefault: false,
    gracePeriodDays: 0,
  });

  const [installments, setInstallments] = useState<InstallmentForm[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Initialize form with existing data or preset
  useEffect(() => {
    if (schedule) {
      setFormData({
        name: schedule.name,
        code: schedule.code,
        type: schedule.type,
        description: schedule.description || '',
        isDefault: schedule.isDefault,
        gracePeriodDays: schedule.gracePeriodDays,
      });
      setInstallments(
        schedule.installments.map((inst) => ({
          id: inst.id,
          label: inst.label,
          percentageOfTotal: inst.percentageOfTotal,
          dateType: inst.relativeDays !== null ? 'relative' : 'fixed',
          dueDayOfMonth: inst.dueDayOfMonth ?? undefined,
          dueMonth: inst.dueMonth ?? undefined,
          relativeDays: inst.relativeDays ?? undefined,
        }))
      );
    } else {
      // Default to monthly preset
      setInstallments(PRESET_TEMPLATES.MONTHLY);
    }
  }, [schedule]);

  // When type changes, apply preset template
  const handleTypeChange = (type: PaymentScheduleType) => {
    setFormData({ ...formData, type });
    if (!isEditing) {
      setInstallments(PRESET_TEMPLATES[type]);
    }
  };

  // Add new installment
  const addInstallment = () => {
    setInstallments([
      ...installments,
      {
        label: `Échéance ${installments.length + 1}`,
        percentageOfTotal: 0,
        dateType: 'fixed',
        dueDayOfMonth: 1,
        dueMonth: 9,
      },
    ]);
  };

  // Remove installment
  const removeInstallment = (index: number) => {
    if (installments.length <= 1) return;
    setInstallments(installments.filter((_, i) => i !== index));
  };

  // Update installment
  const updateInstallment = (index: number, field: keyof InstallmentForm, value: any) => {
    const newInstallments = [...installments];
    newInstallments[index] = { ...newInstallments[index], [field]: value };
    setInstallments(newInstallments);
  };

  // Calculate total percentage
  const totalPercentage = installments.reduce((sum, inst) => sum + inst.percentageOfTotal, 0);

  // Validate form
  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('Le nom est obligatoire');
      return false;
    }

    if (installments.length === 0) {
      setError('Au moins une échéance est requise');
      return false;
    }

    // Check percentage total
    if (Math.abs(totalPercentage - 100) > 0.01) {
      setError(`Le total des pourcentages doit être égal à 100% (actuellement ${totalPercentage.toFixed(1)}%)`);
      return false;
    }

    // Check each installment has valid date
    for (let i = 0; i < installments.length; i++) {
      const inst = installments[i];
      if (inst.dateType === 'fixed') {
        if (!inst.dueDayOfMonth || !inst.dueMonth) {
          setError(`L'échéance ${i + 1} nécessite un jour et un mois`);
          return false;
        }
      } else {
        if (inst.relativeDays === undefined || inst.relativeDays < 0) {
          setError(`L'échéance ${i + 1} nécessite un nombre de jours valide`);
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    try {
      const installmentData: CreateInstallmentData[] = installments.map((inst, index) => ({
        sequenceNumber: index + 1,
        label: inst.label,
        percentageOfTotal: inst.percentageOfTotal,
        ...(inst.dateType === 'fixed'
          ? { dueDayOfMonth: inst.dueDayOfMonth, dueMonth: inst.dueMonth }
          : { relativeDays: inst.relativeDays }),
      }));

      const data: CreatePaymentScheduleData = {
        name: formData.name.trim(),
        code: formData.code.trim() || undefined,
        type: formData.type,
        description: formData.description.trim() || undefined,
        isDefault: formData.isDefault,
        gracePeriodDays: formData.gracePeriodDays,
        installments: installmentData,
      };

      if (isEditing && schedule) {
        const res = await updateMutation.mutateAsync({ id: schedule.id, data }) as { studentsUpdating?: number };
        // When students are assigned to this template, they're regenerated in
        // the background — confirm it so the edit doesn't look like a no-op.
        if (res?.studentsUpdating && res.studentsUpdating > 0) {
          setInfo(`Échéancier mis à jour. ${res.studentsUpdating} étudiant(s) assigné(s) sont en cours de mise à jour (les échéances sont recalculées, les paiements conservés).`);
          setTimeout(() => {
            onSuccess();
            onClose();
          }, 2800);
          return;
        }
      } else {
        await createMutation.mutateAsync(data);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEditing ? "Modifier l'échéancier" : 'Nouvel échéancier'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle size={18} />
              {error}
            </div>
          )}
          {info && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              <AlertCircle size={18} />
              {info}
            </div>
          )}

          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Ex: Mensuel 10 mois"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Auto-généré si vide"
              />
            </div>
          </div>

          {/* Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <div className="flex flex-wrap gap-2">
              {SCHEDULE_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => handleTypeChange(type.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    formData.type === type.value
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Description optionnelle..."
            />
          </div>

          {/* Grace Period & Default */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Délai de grâce (jours)
              </label>
              <input
                type="number"
                min="0"
                max="30"
                value={formData.gracePeriodDays}
                onChange={(e) =>
                  setFormData({ ...formData, gracePeriodDays: parseInt(e.target.value) || 0 })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Jours avant de marquer une échéance comme "en retard"
              </p>
            </div>
            <div className="flex items-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">Définir comme échéancier par défaut</span>
              </label>
            </div>
          </div>

          {/* Installments Section */}
          <div className="border-t border-gray-200 pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-md font-semibold text-gray-900">
                Échéances ({installments.length})
              </h3>
              <div className="flex items-center gap-4">
                <span
                  className={`text-sm font-medium ${
                    Math.abs(totalPercentage - 100) <= 0.01 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  Total: {totalPercentage.toFixed(1)}%
                </span>
                <button
                  type="button"
                  onClick={addInstallment}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors"
                >
                  <Plus size={16} />
                  Ajouter
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {installments.map((inst, index) => (
                <div
                  key={index}
                  className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Échéance #{index + 1}
                    </span>
                    {installments.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeInstallment(index)}
                        className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Libellé</label>
                      <input
                        type="text"
                        value={inst.label}
                        onChange={(e) => updateInstallment(index, 'label', e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Pourcentage</label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={inst.percentageOfTotal}
                          onChange={(e) =>
                            updateInstallment(index, 'percentageOfTotal', parseFloat(e.target.value) || 0)
                          }
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 pr-8"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                          %
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Type de date</label>
                      <select
                        value={inst.dateType}
                        onChange={(e) =>
                          updateInstallment(index, 'dateType', e.target.value as 'fixed' | 'relative')
                        }
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                      >
                        <option value="fixed">Date fixe</option>
                        <option value="relative">Jours relatifs</option>
                      </select>
                    </div>
                  </div>

                  {/* Date configuration based on type */}
                  {inst.dateType === 'fixed' ? (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          <Calendar size={12} className="inline mr-1" />
                          Jour du mois
                        </label>
                        <select
                          value={inst.dueDayOfMonth || 1}
                          onChange={(e) =>
                            updateInstallment(index, 'dueDayOfMonth', parseInt(e.target.value))
                          }
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                        >
                          {Array.from({ length: 31 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {i + 1}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Mois</label>
                        <select
                          value={inst.dueMonth || 9}
                          onChange={(e) =>
                            updateInstallment(index, 'dueMonth', parseInt(e.target.value))
                          }
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                        >
                          {MONTHS.map((m) => (
                            <option key={m.value} value={m.value}>
                              {m.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        <Clock size={12} className="inline mr-1" />
                        Jours après le début de l'année
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={inst.relativeDays || 0}
                        onChange={(e) =>
                          updateInstallment(index, 'relativeDays', parseInt(e.target.value) || 0)
                        }
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              disabled={isSubmitting}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting || Math.abs(totalPercentage - 100) > 0.01}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Enregistrement...' : isEditing ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentScheduleFormModal;
